if (!customElements.get('comparison-cost-calculator')) {
  customElements.define('comparison-cost-calculator', class extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      this.prices = JSON.parse(this.querySelector('[data-tier-prices]').textContent);
      this.competitor = JSON.parse(this.querySelector('[data-competitor-pricing]').textContent);
      this.market = this.dataset.market || 'USD';
      if (this.competitor.monthly === null) {
        this.showRange();
        return;
      }
      this.rate = this.prices.currency === 'USD' ? 1 : null;
      this.maxPeople = Math.min(this.competitor.comparisonMaximum, Number(this.prices.maximums.at(-1)));
      this.slider = this.querySelector('input[type="range"]');
      this.count = this.querySelector('[data-team-count]');
      for (const input of [this.slider, this.count]) input.max = String(this.maxPeople);
      this.put('[data-team-max]', `${this.maxPeople} people`);
      this.put('[data-range-limit]', this.competitor.limitNote);
      this.slider.addEventListener('input', () => this.update());
      this.slider.addEventListener('change', () => this.announce());
      this.count.addEventListener('input', () => {
        if (!this.count.validity.valid || !this.count.value) return;
        this.slider.value = this.count.value;
        this.update();
      });
      const commitCount = () => {
        const count = Number(this.count.value);
        this.slider.value = this.count.value && Number.isFinite(count)
          ? this.normalize(count) : this.slider.value;
        this.update();
        this.announce();
      };
      this.count.addEventListener('change', commitCount);
      this.count.addEventListener('blur', commitCount);
      this.update();
      if (this.rate === null) this.convert();
    }
    normalize(count) { return Math.max(1, Math.min(this.maxPeople, Math.round(count))); }
    showRange() {
      const {rangeMinimum: minimum, rangeMaximum: maximum} = this.competitor;
      let lowest = Infinity, highest = 0, tierMinimum = 1;
      // Test both ends of each occupied tier; a tier price is not linear per user.
      this.prices.maximums.forEach((tierMaximum, index) => {
        const start = Math.max(minimum, tierMinimum);
        const end = Math.min(maximum, Number(tierMaximum));
        const fee = Number(this.prices.fees[index]);
        if (start <= end) {
          lowest = Math.min(lowest, fee / end / 12);
          highest = Math.max(highest, fee / start / 12);
        }
        tierMinimum = Number(tierMaximum) + 1;
      });
      this.put('[data-tapt-range]', `${this.money(lowest, this.prices.currency, 2)}–${this.money(highest, this.prices.currency, 2)}`);
      this.put('[data-range-scope]', `Monthly equivalent per profile across ${minimum}–${maximum} profiles, billed annually. Your rate depends on your team size and pricing tier.`);
      this.put('[data-cost-note]', `Tapt’s range uses its current ${this.prices.currency} platform tiers for ${minimum}–${maximum} profiles. Physical cards are separate. ${this.competitor.note} No competitor total or savings is estimated without a comparable quote.`);
    }
    async convert() {
      try {
        const response = await fetch(`https://api.frankfurter.dev/v2/rate/USD/${encodeURIComponent(this.prices.currency)}`, {signal: AbortSignal.timeout(6000)});
        if (!response.ok) throw new Error('Rate unavailable');
        const {rate} = await response.json();
        if (!Number.isFinite(rate) || rate <= 0) throw new Error('Invalid rate');
        this.rate = rate;
      } catch { this.rate = null; }
      this.update();
    }
    money(value, currency, digits = 0) {
      const foreign = currency !== this.market;
      return new Intl.NumberFormat(document.documentElement.lang || 'en', {style:'currency', currency, currencyDisplay:foreign ? 'code' : 'narrowSymbol', minimumFractionDigits:digits, maximumFractionDigits:digits}).format(value);
    }
    put(selector, text) { this.querySelector(selector).textContent = text; }
    announce() {
      this.put('[data-cost-announcement]', `${this.slider.value} people. Tapt: ${this.querySelector('[data-tapt-total]').textContent}. ${this.competitor.name}: ${this.querySelector('[data-competitor-total]').textContent}. Annual base platform pricing.`);
    }
    update() {
      const people = this.normalize(Number(this.slider.value));
      this.slider.value = String(people);
      const tier = this.prices.maximums.findIndex(max => people <= Number(max));
      const annual = Number(this.prices.fees[tier]);
      const currency = this.prices.currency;
      const provider = this.competitor;
      const competitorCurrency = this.rate === null ? 'USD' : currency;
      const seats = Math.max(provider.minimum, people);
      const competitorAnnual = seats * provider.monthly * 12 * (this.rate ?? 1);
      const approximate = currency !== 'USD' && this.rate !== null ? '≈ ' : '';
      if (this.count.value !== String(people)) this.count.value = String(people);
      this.slider.setAttribute('aria-valuetext', `${people} ${people === 1 ? 'person' : 'people'}`);
      this.put('[data-tapt-total]', annual === 0 ? 'Free' : this.money(annual, currency));
      this.put('[data-tapt-period]', annual === 0 ? 'Platform access for 1–4 profiles' : 'per year for your team');
      this.put('[data-tapt-plan]', annual === 0 ? 'Free platform plan · up to 4 profiles' : 'Full suite + rollout support');
      this.put('[data-tapt-unit]', `${this.money(annual / people / 12, currency, 2)} / profile / month equivalent`);
      this.put('[data-competitor-total]', `${approximate}${this.money(competitorAnnual, competitorCurrency)}`);
      this.put('[data-competitor-period]', 'base fee per year, billed annually');
      this.put('[data-competitor-unit]', `${approximate}${this.money(provider.monthly * (this.rate ?? 1), competitorCurrency, 2)} / user / month`);
      this.put('[data-competitor-plan]', `${provider.plan} plan${people < provider.minimum ? ` · ${provider.minimum}-seat minimum` : ''}`);
      this.put('[data-saving]', this.rate !== null && competitorAnnual > annual ? `Annual base-fee difference: ${this.money(competitorAnnual - annual, currency)}` : '');
      const conversion = this.rate === null ? ` Currency conversion is unavailable; ${provider.name} is shown in USD.` : currency !== 'USD' ? ' Competitor prices are indicative conversions from USD.' : '';
      const minimum = people < provider.minimum ? ` This compares the ${provider.minimum}-seat team minimum with Tapt’s ${people}-profile fee.` : '';
      this.put('[data-cost-note]', `Tapt uses its current ${currency} platform tiers. Physical cards are separate. ${provider.note}${conversion}${minimum} Feature inclusions differ; the base-fee difference is not a like-for-like full-service quote.`);
    }
  });
}
